import { call, delay, fork, put, select, spawn, take, takeEvery, takeLatest } from 'redux-saga/effects';
import axios, { AxiosResponse } from 'axios';
import { offlineActions, offlineSelectors, offlineTypes } from './index';
import { EventChannel, eventChannel } from 'redux-saga';
import { AnyAction } from 'redux';
import queueSelectors from './queue.selectors';
import { notificationActions } from '../notification';
import { NotificationType } from '../../screens/MainScreen/NotificationWrapper/NotificationWrapper';

function* checkOffline() {
    const detectOfflineChannel: EventChannel<boolean> = yield call(detectOffline);

    while (true) {
        let isOnline: boolean = yield take(detectOfflineChannel);

        if (isOnline) {
            try {
                let response: AxiosResponse = yield axios.get(`/api/public/application`);
                if (response.status === 200) {
                    yield put(offlineActions.setOnlineStatus(true));
                }
            } catch (e) {
                yield put(offlineActions.setOnlineStatus(false));
            }
        } else {
            yield put(offlineActions.setOnlineStatus(false));
        }
    }
}

let isSendingQueue = false;

function* sendQueue() {
    if (isSendingQueue) return;
    isSendingQueue = true;
    try {
        yield call(_sendQueue);
    } finally {
        isSendingQueue = false;
    }
}

function* _sendQueue() {
    const queueLength: number = yield select(queueSelectors.getQueueLength);
    if (queueLength === 0) {
        yield put(offlineActions.queueTrySendDone());
        return;
    }

    const isOnline: boolean = yield select(offlineSelectors.isOnline);

    if (isOnline) {
        const queuedActions: AnyAction[] = yield select(queueSelectors.getQueuedActions);
        const totalItems = queuedActions.length;
        for (let queuedAction of queuedActions) {
            yield put({ ...queuedAction, _requeued: true });
            yield put(offlineActions.queueActionSent(queuedAction));
            yield delay(300);
        }
        // Grace window: assumes failed requests re-queue within 500 ms.
        // On slow connections this may undercount failures — pre-existing limitation.
        yield delay(500);

        const remainingItems: number = yield select(queueSelectors.getQueueLength);
        if (remainingItems === 0) {
            const body = totalItems === 1
                ? 'Vielen Dank. Ihr Zählerstand wurde erfolgreich übermittelt.'
                : 'Vielen Dank. Ihre Zählerstände wurden erfolgreich übermittelt.';
            yield put(notificationActions.showNotification(
                'Ablesungen erfolgreich übermittelt',
                body,
                NotificationType.SUCCESS,
                'queue-sync',
                undefined,
                'replace',
            ));
        } else {
            yield put(notificationActions.showNotification(
                'Ablesungen konnten nicht übermittelt werden',
                'Ihre Ablesungen werden automatisch erneut gesendet, sobald eine Verbindung besteht.',
                NotificationType.WARNING,
                'queue-sync',
                undefined,
                'replace',
            ));
        }

        yield put(offlineActions.queueTrySendDone());
    } else {
        yield delay(2000);
        yield put(offlineActions.queueTrySendError());
    }
}

function detectOffline() {
    return eventChannel((emitter) => {
        window.addEventListener('online', () => {
            emitter(true);
        });
        window.addEventListener('offline', () => {
            emitter(false);
        });

        return () => {};
    });
}

function* showNotification(isOnline: boolean) {
    if (isOnline) {
        // Wenn Queue-Items vorhanden: sendQueue übernimmt das Feedback (SUCCESS/WARNING).
        // Verbindungs-Notification würde doppelt erscheinen — hier nichts zeigen.
        const queueLength: number = yield select(queueSelectors.getQueueLength);
        if (queueLength > 0) return;

        yield put(
            notificationActions.showInfoNotification(
                'Internet verfügbar!',
                'Alle Funktionen stehen wieder zur Verfügung.',
                'connection',
                5000,
                'replace',
            ),
        );
    } else {
        yield put(
            notificationActions.showInfoNotification(
                'Kein Internet',
                'Ablesungen können weiterhin durchgeführt werden!',
                'connection',
                5000,
                'replace',
            ),
        );
    }
}

function* watchOnlineStatus() {
    let previousStatus: boolean = yield select(offlineSelectors.isOnline);

    while (true) {
        let { isOnline } = yield take(offlineTypes.SET_ONLINE_STATUS);

        if (previousStatus !== isOnline) {
            const queueLengthAtConnect: number = isOnline ? yield select(queueSelectors.getQueueLength) : 0;
            yield delay(1500);
            const currentStatus: boolean = yield select(offlineSelectors.isOnline);
            if (currentStatus === isOnline && (queueLengthAtConnect === 0 || !isOnline)) {
                yield showNotification(isOnline);
            }
            previousStatus = currentStatus;
        }
    }
}

function* pingOnline() {
    while (true) {
        try {
            let fetchResponse: Response = yield fetch('/api/public/application', {
                method: 'HEAD',
            });
            if (fetchResponse.ok) {
                yield put(offlineActions.setOnlineStatus(true));
            } else {
                yield put(offlineActions.setOnlineStatus(false));
            }
        } catch (e) {
            yield put(offlineActions.setOnlineStatus(false));
        }
        yield delay(30000);
    }
}

export default function* offlineSaga() {
    yield fork(watchOnlineStatus);
    yield spawn(checkOffline);

    yield takeLatest(offlineTypes.QUEUE_TRY_SEND, sendQueue);
    yield takeEvery(offlineTypes.SET_ONLINE_STATUS, sendQueue);
    // yield takeLatest(offlineTypes.SET_ONLINE_STATUS, showNotification);

    yield spawn(sendQueue);

    /**
     * initial check for online status
     */
    // if (!window.navigator.onLine) {
    //     yield put(offlineActions.setOnlineStatus(false));
    // }

    // try {
    //     let fetchResponse: Response = yield fetch('/api/public/application', { method: 'HEAD' });
    //     if (fetchResponse.ok) {
    //         yield put(offlineActions.setOnlineStatus(true));
    //     } else {
    //         yield put(offlineActions.setOnlineStatus(false));
    //     }
    // } catch (e) {
    //     yield put(offlineActions.setOnlineStatus(false));
    // }

    yield spawn(pingOnline);
}
